/* © NXAPP Limited */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuthContext } from '@/context/AuthContext'
import AdminLayout from '@/components/layout/AdminLayout'
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import DashboardPage from '@/pages/DashboardPage'
import ContactsPage from '@/pages/contacts/ContactsPage'
import SuppressionPage from '@/pages/suppression/SuppressionPage'
import SegmentsPage from '@/pages/segments/SegmentsPage'
import CampaignsPage from '@/pages/campaigns/CampaignsPage'
import CampaignDetailPage from '@/pages/campaigns/CampaignDetailPage'
import IssueEditorPage from '@/pages/issues/IssueEditorPage'
import IssueStatsPage from '@/pages/issues/IssueStatsPage'
import TemplatesPage from '@/pages/templates/TemplatesPage'
import TemplateEditorPage from '@/pages/templates/TemplateEditorPage'
import AiDraftPage from '@/pages/ai/AiDraftPage'
import LeadsPage from '@/pages/leads/LeadsPage'
import LeadsImportPage from '@/pages/leads/LeadsImportPage'
import MediaResponsePage from '@/pages/media/MediaResponsePage'
import LettersPage from '@/pages/letters/LettersPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import ProfilePage from '@/pages/settings/ProfilePage'
import GuidePage from '@/pages/guide/GuidePage'
import ProspectPage from '@/pages/prospect/ProspectPage'
import TMPage from '@/pages/tm/TMPage'
import TermsPage from '@/pages/legal/TermsPage'
import PrivacyPage from '@/pages/legal/PrivacyPage'
import UnsubscribePage from '@/pages/public/UnsubscribePage'
import SubscribePage from '@/pages/public/SubscribePage'
import ErrorBoundary from '@/components/ui/ErrorBoundary'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthContext()
  if (loading) return <div className="flex h-screen items-center justify-center text-gray-500 text-sm">Loading…</div>
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Admin (protected) */}
            <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<DashboardPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="suppression" element={<SuppressionPage />} />
              <Route path="segments" element={<SegmentsPage />} />

              {/* Campaigns + scoped issues/templates */}
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="campaigns/:campaignId" element={<CampaignDetailPage />} />
              <Route path="campaigns/:campaignId/issues/new" element={<IssueEditorPage />} />
              <Route path="campaigns/:campaignId/issues/:id/edit" element={<IssueEditorPage />} />
              <Route path="campaigns/:campaignId/issues/:id/stats" element={<IssueStatsPage />} />
              <Route path="campaigns/:campaignId/templates/new" element={<TemplateEditorPage />} />
              <Route path="campaigns/:campaignId/templates/:id/edit" element={<TemplateEditorPage />} />

              {/* Global shared templates */}
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="templates/new" element={<TemplateEditorPage />} />
              <Route path="templates/:id/edit" element={<TemplateEditorPage />} />

              {/* Legacy /issues redirect */}
              <Route path="issues" element={<Navigate to="/campaigns" replace />} />
              <Route path="issues/*" element={<Navigate to="/campaigns" replace />} />

              <Route path="ai-draft" element={<AiDraftPage />} />
              <Route path="media" element={<MediaResponsePage />} />
              <Route path="letters" element={<LettersPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="leads-import" element={<LeadsImportPage />} />
              <Route path="prospect" element={<ProspectPage />} />
              <Route path="tm" element={<TMPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="guide" element={<GuidePage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  )
}
